import { useCallback, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { parseISO } from "date-fns";
import { useIsMutating } from "@tanstack/react-query";
import {
    ActionButton,
    Button,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    FormCheckbox,
    Input,
    RadioInput,
    Select,
    TextArea,
} from "@jhool-io/fe-components";
import {
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import {
    IEditDocumentationRequestNoteMedPayload,
    IGetClientNotes,
    ISessionNote,
    SessionLocations,
} from "../../../../modules/notes/types/notes.types";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import { DOCUMENTATION_REQUEST_NOTE_MED_LABELS } from "../../../../utils/constants";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import ViewFile from "../../../../shared-ui/ViewFile/ViewFile";
import DiagnosisCodeSelect from "../../../../modules/notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";

import * as FilePicker from "../../../../shared-ui/FilePicker/FilePicker";
import * as Progress from "../../../../shared-ui/Progress/Progress";
import DeleteIcon from "../../../../icons/Delete";
import { IViewFile } from "../../../../utils/types";
import MedNoteEPrescribe from "../../../../modules/e-prescirbe/components/MedNoteEPrescribe/MedNoteEPrescribe";

interface EditDocumentationRequestNoteMedProps {
    noteDetails: ISessionNote;
}

export default function EditDocumentationRequestNoteMed({
    noteDetails,
}: EditDocumentationRequestNoteMedProps) {
    const [showPrompt, setShowPrompt] = useState(false);
    const [selectedNote, setSelectedNote] = useState({} as IGetClientNotes);
    const [docInView, setDocInView] = useState<IViewFile | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
    const [
        localRecordOfDisclosureOfPhiEphi,
        setLocalRecordOfDisclosureOfPhiEphi,
    ] = useState<Date | null>(
        noteDetails.note_content?.record_of_disclosure_of_phi_ephi
            ? parseISO(
                  noteDetails.note_content.record_of_disclosure_of_phi_ephi
              )
            : null
    );
    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };

    const isSigning = useRef(false);

    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );

    const {
        handleAutoSaveNote,
        handleEditDraftNote,
        handleSignNote,
        handleAddNoteDocument,
        handleDeleteNoteContentDocument,
        uploadProgress,
    } = useEditNote();

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        watch,
        control,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditDocumentationRequestNoteMedPayload>({
        resolver: yupResolver(
            yup.object({
                location_of_the_client: yup
                    .string()
                    .nullable()
                    .when("session_location", (sessionLocation, field) =>
                        sessionLocation === SessionLocations.Telehealth &&
                        isSigning.current
                            ? field.required("Client location is required")
                            : field
                    ),
                other_location_of_the_client: yup
                    .string()
                    .nullable()
                    .when(["session_location", "location_of_the_client"], {
                        is: (sessionLocation?: string, location?: string) =>
                            sessionLocation === SessionLocations.Telehealth &&
                            location === "other" &&
                            isSigning.current,
                        then: yup
                            .string()
                            .nullable()
                            .trim()
                            .required("Other client location is required"),
                        otherwise: yup.string().nullable(),
                    }),
                location_of_the_provider: yup
                    .string()
                    .nullable()
                    .when("session_location", (sessionLocation, field) =>
                        sessionLocation === SessionLocations.Telehealth &&
                        isSigning.current
                            ? field.required("Provider location is required")
                            : field
                    ),
                other_location_of_the_provider: yup
                    .string()
                    .nullable()
                    .when(["session_location", "location_of_the_provider"], {
                        is: (sessionLocation?: string, location?: string) =>
                            sessionLocation === SessionLocations.Telehealth &&
                            location === "other" &&
                            isSigning.current,
                        then: yup
                            .string()
                            .nullable()
                            .trim()
                            .required("Other provider location is required"),
                        otherwise: yup.string().nullable(),
                    }),
                consent_was_received_for_the_teletherapy_session: yup.bool(),
                the_session_was_conducted_via_video: yup.bool(),
                session_location: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .nullable()
                        .trim()
                        .required("Session location is required"),
                    otherwise: yup.string(),
                }),
                diagnosis_codes: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .array()
                            .min(1, "Primary diagnosis is required"),
                        otherwise: yup.array(),
                    }),
                secondary_diagnosis: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                tertiary_diagnosis: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                additional_diagnosis_one: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                additional_diagnosis_two: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                record_of_disclosure_of_phi_ephi: yup
                    .string()
                    .nullable()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.string().required("This field is required"),
                        otherwise: yup.string().nullable(),
                    }),
                disclosure_to_whom: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                purpose_of_disclosure: yup.string().nullable(),
                description_disclosure_purpose_of_disclosure: yup
                    .string()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .trim()
                            .required("This field is required"),
                        otherwise: yup.string(),
                    }),
                type_disclosed: yup.array().nullable(),
                how_disclosure_is_made: yup.array().nullable(),
                other_how_disclosure_is_made: yup.string(),
                /*
                start_time: yup.string().nullable(),
                end_time: yup
                    .string()
                    .nullable()
                    .test(
                        "end-time-after-start-time",
                        "End time cannot be earlier than start time",
                        (endTime, context) => {
                            const { start_time: startTime } = context.parent;

                            return (
                                !startTime || !endTime || endTime >= startTime
                            );
                        }
                    ),
                */
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().trim().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: noteDetails.note_content
            ? {
                  ...noteDetails.note_content,
                  secondary_diagnosis: noteDetails?.note_content
                      ?.secondary_diagnosis
                      ? [noteDetails?.note_content?.secondary_diagnosis]
                      : undefined,
                  tertiary_diagnosis: noteDetails?.note_content
                      ?.tertiary_diagnosis
                      ? [noteDetails?.note_content?.tertiary_diagnosis]
                      : undefined,
                  additional_diagnosis_one: noteDetails?.note_content
                      ?.additional_diagnosis_one
                      ? [noteDetails?.note_content?.additional_diagnosis_one]
                      : undefined,
                  additional_diagnosis_two: noteDetails?.note_content
                      ?.additional_diagnosis_two
                      ? [noteDetails?.note_content?.additional_diagnosis_two]
                      : undefined,
                  record_of_disclosure_of_phi_ephi:
                      noteDetails.note_content
                          .record_of_disclosure_of_phi_ephi ?? null,
                  purpose_of_disclosure:
                      noteDetails.note_content.purpose_of_disclosure ?? null,
                  type_disclosed:
                      noteDetails.note_content.type_disclosed ?? null,
                  how_disclosure_is_made:
                      noteDetails.note_content.how_disclosure_is_made ?? null,
                  start_time: noteDetails.note_content.start_time ?? null,
                  end_time: noteDetails.note_content.end_time ?? null,
                  signature: noteDetails.note_content.signature ?? "",
              }
            : undefined,
        shouldFocusError: false,
    });

    const watchedValues = watch();

    const isAddDocMutating = useIsMutating(["add-note-content-document"]);
    const isDeleteDocMutating = useIsMutating(["delete-note-content-document"]);

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...acceptedFiles, ...selectedFiles]);
            } else {
                setSelectedFiles([...acceptedFiles]);
            }
        },
        [selectedFiles]
    );

    const handleRemoveSelectedFile = (filename: string) => {
        if (selectedFiles) {
            const filesArr = [...selectedFiles].filter(
                (file) => file.name !== filename
            );
            if (filesArr.length === 0) setSelectedFiles(null);
            else setSelectedFiles(filesArr);
        }
    };

    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue(
            "location_of_the_client",
            note.note_content.location_of_the_client
        );
        setValue(
            "other_location_of_the_client",
            note.note_content.other_location_of_the_client
        );
        setValue(
            "location_of_the_provider",
            note.note_content.location_of_the_provider
        );
        setValue(
            "other_location_of_the_provider",
            note.note_content.other_location_of_the_provider
        );
        setValue(
            "consent_was_received_for_the_teletherapy_session",
            note.note_content.consent_was_received_for_the_teletherapy_session
        );
        setValue(
            "the_session_was_conducted_via_video",
            note.note_content.the_session_was_conducted_via_video
        );

        setValue("session_location", note.note_content.session_location ?? "");
        const recordDate =
            note.note_content.record_of_disclosure_of_phi_ephi ?? null;
        setValue("diagnosis_codes", note.note_content.diagnosis_codes);
        setValue(
            "secondary_diagnosis",
            note.note_content.secondary_diagnosis
                ? [note.note_content.secondary_diagnosis]
                : undefined
        );
        setValue(
            "tertiary_diagnosis",
            note.note_content.tertiary_diagnosis
                ? [note.note_content.tertiary_diagnosis]
                : undefined
        );
        setValue(
            "additional_diagnosis_one",
            note.note_content.additional_diagnosis_one
                ? [note.note_content.additional_diagnosis_one]
                : undefined
        );
        setValue(
            "additional_diagnosis_two",
            note.note_content.additional_diagnosis_two
                ? [note.note_content.additional_diagnosis_two]
                : undefined
        );
        setValue("record_of_disclosure_of_phi_ephi", recordDate);
        setLocalRecordOfDisclosureOfPhiEphi(
            recordDate ? parseISO(recordDate) : null
        );
        setValue("disclosure_to_whom", note.note_content.disclosure_to_whom);
        setValue(
            "purpose_of_disclosure",
            note.note_content.purpose_of_disclosure ?? null
        );
        setValue(
            "description_disclosure_purpose_of_disclosure",
            note.note_content.description_disclosure_purpose_of_disclosure
        );
        setValue("type_disclosed", note.note_content.type_disclosed ?? null);
        setValue(
            "how_disclosure_is_made",
            note.note_content.how_disclosure_is_made ?? null
        );
        setValue("start_time", note.note_content.start_time ?? null);
        setValue("end_time", note.note_content.end_time ?? null);
        setShowPrompt(false);
    };

    const handleIsChecked = (checkboxValue: string, values?: string[]) => {
        return Boolean(
            values?.find(
                (value: string) => value.trim() === checkboxValue.trim()
            )
        );
    };

    const sessionLocationOptionsForSelect = Object.keys(SessionLocations).map(
        (location) => ({
            label: removeEnumUnderscore(location),
            value: SessionLocations[location as keyof typeof SessionLocations],
        })
    );

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <>
            {docInView ? (
                <Dialog open={Boolean(docInView)}>
                    <DialogContent
                        title={
                            docInView?.document_name ??
                            "Note content attachment"
                        }
                        handleCloseDialog={() => {
                            setDocInView(null);
                        }}
                        showFooter={false}
                    >
                        <ViewFile
                            url={docInView.url}
                            contentType={
                                docInView?.metadata?.content_type ?? ""
                            }
                            title={
                                docInView?.document_name ??
                                "Note content attachment"
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />

            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}

            <form
                onSubmit={handleSubmit(handleSignNote)}
                aria-label="documentation request medical note form"
                id="edit-note-form"
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>
                <div className="fg">
                    <Controller
                        name="session_location"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={
                                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.session_location
                                }
                                placeholder=""
                                options={sessionLocationOptionsForSelect}
                                onChange={(val) =>
                                    field.onChange(
                                        (val as { value: string }).value
                                    )
                                }
                                value={
                                    sessionLocationOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                hasError={!!errors.session_location}
                                errorText={errors.session_location?.message}
                            />
                        )}
                    />
                </div>
                {watch("session_location") === SessionLocations.Telehealth && (
                    <>
                        <h3 className="note-label-req">
                            {
                                DOCUMENTATION_REQUEST_NOTE_MED_LABELS.location_of_the_client
                            }
                        </h3>
                        <div className="fg flex gap-x-5">
                            {["home", "office", "other"].map((value) => (
                                <RadioInput
                                    key={value}
                                    {...register("location_of_the_client")}
                                    label={makeStringFirstLetterCapital(value)}
                                    value={value}
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.location_of_the_client === value
                                    }
                                />
                            ))}
                        </div>
                        {errors.location_of_the_client ? (
                            <div className="note-error">
                                <p>{errors.location_of_the_client?.message}</p>
                            </div>
                        ) : null}
                        {watch("location_of_the_client") === "other" && (
                            <div className="fg">
                                <Input
                                    {...register(
                                        "other_location_of_the_client"
                                    )}
                                    label={
                                        DOCUMENTATION_REQUEST_NOTE_MED_LABELS.other_location_of_the_client
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_location_of_the_client
                                    }
                                    isRequired
                                    hasError={
                                        !!errors.other_location_of_the_client
                                    }
                                    errorText={
                                        errors.other_location_of_the_client
                                            ?.message
                                    }
                                />
                            </div>
                        )}
                        <h3 className="note-label-req">
                            {
                                DOCUMENTATION_REQUEST_NOTE_MED_LABELS.location_of_the_provider
                            }
                        </h3>
                        <div className="fg flex gap-x-5">
                            {["home", "office", "other"].map((value) => (
                                <RadioInput
                                    key={value}
                                    {...register("location_of_the_provider")}
                                    label={makeStringFirstLetterCapital(value)}
                                    value={value}
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.location_of_the_provider === value
                                    }
                                />
                            ))}
                        </div>
                        {errors.location_of_the_provider ? (
                            <div className="note-error">
                                <p>
                                    {errors.location_of_the_provider?.message}
                                </p>
                            </div>
                        ) : null}
                        {watch("location_of_the_provider") === "other" && (
                            <div className="fg">
                                <Input
                                    {...register(
                                        "other_location_of_the_provider"
                                    )}
                                    label={
                                        DOCUMENTATION_REQUEST_NOTE_MED_LABELS.other_location_of_the_provider
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_location_of_the_provider
                                    }
                                    isRequired
                                    hasError={
                                        !!errors.other_location_of_the_provider
                                    }
                                    errorText={
                                        errors.other_location_of_the_provider
                                            ?.message
                                    }
                                />
                            </div>
                        )}
                        <div className="note-checkboxes">
                            <FormCheckbox
                                {...register(
                                    "consent_was_received_for_the_teletherapy_session"
                                )}
                                label={
                                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.consent_was_received_for_the_teletherapy_session
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.consent_was_received_for_the_teletherapy_session
                                }
                            />
                            <FormCheckbox
                                {...register(
                                    "the_session_was_conducted_via_video"
                                )}
                                label={
                                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.the_session_was_conducted_via_video
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.the_session_was_conducted_via_video
                                }
                            />
                        </div>
                    </>
                )}

                <div className="fg">
                    <Controller
                        name="diagnosis_codes"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.diagnosis_codes
                                }
                                value={field.value}
                                onChange={field.onChange}
                                isRequired
                                errorText={
                                    errors.diagnosis_codes?.type === "typeError"
                                        ? "Diagnosis codes are required"
                                        : errors.diagnosis_codes?.message
                                }
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="secondary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.secondary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.secondary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="tertiary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.tertiary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.tertiary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_one"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.additional_diagnosis_one
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_one?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_two"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.additional_diagnosis_two
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_two?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>

                <MedNoteEPrescribe
                    noteDetails={noteDetails}
                    showDiagnosisSync
                />
                <div className="fg">
                    <Controller
                        name="record_of_disclosure_of_phi_ephi"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                isRequired
                                label={
                                    DOCUMENTATION_REQUEST_NOTE_MED_LABELS.record_of_disclosure_of_phi_ephi
                                }
                                onChange={(date) => {
                                    field.onChange(date ?? null);
                                    setLocalRecordOfDisclosureOfPhiEphi(date);
                                }}
                                value={
                                    field.value
                                        ? formatDate(field.value, true)
                                        : undefined
                                }
                                selected={localRecordOfDisclosureOfPhiEphi}
                                hasError={Boolean(
                                    errors.record_of_disclosure_of_phi_ephi
                                )}
                                errorText={
                                    errors.record_of_disclosure_of_phi_ephi
                                        ?.message
                                }
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("disclosure_to_whom")}
                        label={
                            DOCUMENTATION_REQUEST_NOTE_MED_LABELS.disclosure_to_whom
                        }
                        errorText={errors.disclosure_to_whom?.message}
                        hasError={Boolean(errors.disclosure_to_whom)}
                        isRequired
                    />
                </div>
                <p className="note-label-opt">
                    {
                        DOCUMENTATION_REQUEST_NOTE_MED_LABELS.purpose_of_disclosure
                    }
                </p>
                <div className="fg fg-space-between two flex">
                    <RadioInput
                        {...register("purpose_of_disclosure")}
                        label="Authorized disclosure"
                        value="Authorized disclosure"
                        defaultChecked={
                            noteDetails.note_content.purpose_of_disclosure ===
                            "Authorized disclosure"
                        }
                    />
                    <RadioInput
                        {...register("purpose_of_disclosure")}
                        label="Unauthorized disclosure"
                        value="Unauthorized disclosure"
                        defaultChecked={
                            noteDetails.note_content.purpose_of_disclosure ===
                            "Unauthorized disclosure"
                        }
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register(
                            "description_disclosure_purpose_of_disclosure"
                        )}
                        label={
                            DOCUMENTATION_REQUEST_NOTE_MED_LABELS.description_disclosure_purpose_of_disclosure
                        }
                        hasError={Boolean(
                            errors.description_disclosure_purpose_of_disclosure
                        )}
                        errorText={
                            errors.description_disclosure_purpose_of_disclosure
                                ?.message
                        }
                        isRequired
                    />
                </div>
                <div>
                    <h3 className="note-label note-label-opt">
                        {DOCUMENTATION_REQUEST_NOTE_MED_LABELS.type_disclosed}
                    </h3>
                    <div className="note-checkboxes">
                        {[
                            "Treatment Record",
                            "Payment Information",
                            "Healthcare Operation",
                        ].map((value) => (
                            <FormCheckbox
                                key={value}
                                {...register("type_disclosed")}
                                label={value}
                                value={value}
                                defaultChecked={handleIsChecked(
                                    value,
                                    noteDetails.note_content.type_disclosed
                                )}
                            />
                        ))}
                    </div>
                </div>
                <div>
                    <h3 className="note-label note-label-opt">
                        {
                            DOCUMENTATION_REQUEST_NOTE_MED_LABELS.how_disclosure_is_made
                        }
                    </h3>
                    <div className="note-checkboxes">
                        {[
                            "Fax",
                            "Telephone",
                            "Electronic",
                            "Mail",
                            "Other",
                        ].map((value) => (
                            <FormCheckbox
                                key={value}
                                {...register("how_disclosure_is_made")}
                                label={value}
                                value={value}
                                defaultChecked={handleIsChecked(
                                    value,
                                    noteDetails.note_content
                                        .how_disclosure_is_made
                                )}
                            />
                        ))}
                    </div>
                </div>
                {watchedValues.how_disclosure_is_made?.includes("Other") ? (
                    <div className="fg">
                        <TextArea
                            {...register("other_how_disclosure_is_made")}
                            label={
                                DOCUMENTATION_REQUEST_NOTE_MED_LABELS.other_how_disclosure_is_made
                            }
                            isOptional
                        />
                    </div>
                ) : null}
                {/*
                <div className="fg fg-space-between two flex">
                    <Input
                        {...register("start_time", {
                            onChange: async () => {
                                await trigger("end_time");
                            },
                        })}
                        type="time"
                        label={DOCUMENTATION_REQUEST_NOTE_MED_LABELS.start_time}
                        hasError={Boolean(errors.start_time)}
                        errorText={errors.start_time?.message}
                        isOptional
                    />
                    <Input
                        {...register("end_time")}
                        type="time"
                        label={DOCUMENTATION_REQUEST_NOTE_MED_LABELS.end_time}
                        hasError={Boolean(errors.end_time)}
                        errorText={errors.end_time?.message}
                        isOptional
                    />
                </div>
                */}
                <h3 className="note-label-opt">
                    {DOCUMENTATION_REQUEST_NOTE_MED_LABELS.files}
                </h3>
                <div className="fg flex flex-col gap-y-[--spacing-form-items]">
                    <FilePicker.FilePicker
                        onDrop={onDrop}
                        disabled={isAddDocMutating > 0}
                    >
                        <FilePicker.FilePickerIcon />
                        <FilePicker.FilePickerMeta description="Click to upload note files" />
                    </FilePicker.FilePicker>
                    {selectedFiles && selectedFiles.length > 0 ? (
                        <div className="flex flex-col gap-y-[--spacing-items]">
                            <FilePicker.FilePickerSelectedFiles
                                selectedFiles={selectedFiles}
                                handleRemoveSelectedFile={
                                    handleRemoveSelectedFile
                                }
                                wrapperClassName="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] grid-flow-row flex-row gap-[--spacing-items]"
                            />
                            <Button
                                className="self-start"
                                onClick={() =>
                                    handleAddNoteDocument(selectedFiles, () =>
                                        setSelectedFiles(null)
                                    )
                                }
                                disabled={isAddDocMutating > 0}
                            >
                                Save files
                            </Button>
                            {uploadProgress > 0 && uploadProgress < 100 && (
                                <Progress.Root value={uploadProgress}>
                                    <Progress.Label>
                                        Uploading {selectedFiles.length}{" "}
                                        {selectedFiles.length === 1
                                            ? "file"
                                            : "files"}
                                        ...
                                    </Progress.Label>
                                    <Progress.Value />
                                    <Progress.Track>
                                        <Progress.Indicator />
                                    </Progress.Track>
                                </Progress.Root>
                            )}
                        </div>
                    ) : null}
                    {noteDetails?.note_content_documents &&
                    noteDetails?.note_content_documents.length > 0 ? (
                        <div className="flex flex-col gap-y-[var(--spacing-items)]">
                            {noteDetails.note_content_documents.map((doc) => (
                                <div
                                    key={doc.note_content_document_id}
                                    className="flex items-center gap-x-3"
                                >
                                    <ActionButton
                                        variant="can-hover"
                                        size="auto"
                                        className="h-7 !text-text text-caption underline"
                                        actionTitle="View file"
                                        onClick={() => {
                                            setDocInView(doc);
                                        }}
                                    >
                                        <span>{`${displayNameInRightFormat(
                                            {
                                                firstName:
                                                    doc.uploaded_by.first_name,
                                                lastName:
                                                    doc.uploaded_by.last_name,
                                            },
                                            false
                                        )} -  ${doc.document_name}`}</span>
                                    </ActionButton>
                                    <ActionButton
                                        variant="can-hover"
                                        aria-label="delete icon"
                                        size="auto"
                                        className="h-7"
                                        onClick={() =>
                                            handleDeleteNoteContentDocument(
                                                doc.note_content_document_id
                                            )
                                        }
                                        disabled={isDeleteDocMutating > 0}
                                        actionTitle="Delete file from note content"
                                    >
                                        <DeleteIcon />
                                    </ActionButton>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={Boolean(errors.signature)}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>
                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                    />
                </div>
            </form>
        </>
    );
}
